import DateFnsUtils from '@date-io/date-fns'
import { yupResolver } from '@hookform/resolvers'
import CardContents from '@ifca-root/react-component/src/components/CardList/CardContents'
import MainHeader from '@ifca-root/react-component/src/components/Header/MainHeader'
import { VoiceTextField } from '@ifca-root/react-component/src/components/Input/CustomTextField'
import { ContentWrapper } from '@ifca-root/react-component/src/components/Layout/ContentWrapper'
import Loading from '@ifca-root/react-component/src/components/Loading/Loading'
import { TextField } from '@material-ui/core'
import { Autocomplete } from '@material-ui/lab'
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from '@material-ui/pickers'
import { ErrorDialog } from 'components/Dialog/ErrorDialog'
import { ExitConfirmationDialog } from 'components/Dialog/ExitConfirmationDialog'
import { AccountFooter } from 'components/Footer/AccountFooter'
import SnackBarContext from 'containers/App/Store/SnackBarContext'
import {
  ApprovalStatus,
  useCreateFaAssetRevaluationMutation,
  useGetAssetRegistrationQuery,
  useGetCurrentAssetValueInfoForRevaluationLazyQuery,
  useGetDocumentDateValidationQuery,
  useLatestOpenPeriodCheckingDateQuery,
  useUpdateFaAssetRevaluationMutation,
} from 'generated/graphql'
import { SystemMsgs } from 'helpers/Messages/SystemMsg'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import NumberFormat from 'react-number-format'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import * as yup from 'yup'

interface RevaluationFormData {
  asset_no: any
  asset_name: string
  start_date: Date | null
  model_no: string
  category_name: string
  revaluation_date: Date
  asset_cost: number
  accumulated_depreciation: number
  net_amount_before: number
  net_amount_after: number
  revaluation_surplus: number
  revaluation_deficit: number
  remark: string
}

interface CurrentAssetValueInfo {
  baseOriginalAmount: number
  openingAccumulatedDepreciation: number
  totalIncreaseOriginalValue: number
  totalDecreaseOriginalValue: number
  generatedDepreciation: number
  totalDepreciationAdjustment: number
  totalProvisionImpairment: number
  totalReversalImpairment: number
  totalRevaluationSurplus: number
  totalRevaluationDeficit: number
  currentOriginalValue: number
  currentAccumulatedDepreciation: number
  currentNetBookValue: number
  quantity: number
  residualValue: number
  usefulLife: number
  assetStartDate: string
  lastTransactionDate: string
}

const safeParseNumber = (value: any): number => {
  if (value === undefined || value === null || value === '') return 0
  const parsed =
    typeof value === 'number'
      ? value
      : parseFloat(String(value).replace(/,/g, ''))
  return Number.isFinite(parsed) ? parsed : 0
}

const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)
}

const formatDateForDisplay = (date: Date): string => {
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

export const RevaluationForm = (props: any) => {
  const history = useHistory()
  const location = useLocation()
  const editData: any = location?.state
  const { formMode } = props
  const { CompanyID }: any = useParams()
  const user = JSON.parse(localStorage.getItem('loggedInUser'))

  const [errorDia, setErrorDia] = useState<boolean>(false)
  const [errMsg, setErrMsg] = useState<string>('')
  const [openExitConf, setOpenExitConf] = useState(false)
  const [record, setRecord] = useState(false)
  const [selectedAsset, setSelectedAsset] = useState<any>(null)
  const [
    currentAssetInfo,
    setCurrentAssetInfo,
  ] = useState<CurrentAssetValueInfo | null>(null)
  const { setOpenSnackBar, setSnackBarMsg }: any = useContext(SnackBarContext)

  let mode = formMode
  if (formMode === 'resubmit') {
    mode = 'edit'
  }

  const revaluationSchema = yup.object().shape({
    asset_no: yup.mixed().required('Asset is required'),
    revaluation_date: yup.date().required('Revaluation Date is required'),
    net_amount_after: yup
      .number()
      .transform((value, originalValue) => {
        if (
          originalValue === '' ||
          originalValue === null ||
          originalValue === undefined
        )
          return null
        return typeof originalValue === 'string'
          ? parseFloat(originalValue.replace(/,/g, ''))
          : originalValue
      })
      .nullable()
      .required('Revised Net Book Value is required')
      .min(0, 'Revised Net Book Value cannot be negative'),
    remark: yup.string().nullable(),
  })

  const {
    register,
    setValue,
    control,
    handleSubmit,
    watch,
    getValues,
    errors,
  } = useForm<RevaluationFormData>({
    defaultValues: {
      revaluation_date: editData?.revaluation_date
        ? new Date(editData.revaluation_date)
        : new Date(),
      asset_cost: editData?.asset_cost || 0,
      accumulated_depreciation: editData?.accumulated_depreciation || 0,
      net_amount_before: editData?.net_amount_before || 0,
      net_amount_after: editData?.net_amount_after || 0,
      revaluation_surplus: editData?.revaluation_surplus || 0,
      revaluation_deficit: editData?.revaluation_deficit || 0,
      remark: editData?.remark || '',
    },
    mode: 'onSubmit',
    resolver: yupResolver(revaluationSchema),
  })

  const watchNetAmountAfter = watch('net_amount_after')
  const watchRevaluationDate = watch('revaluation_date')

  const {
    loading: getAssetRegistrationLoading,
    data: { getAssetRegistration } = { getAssetRegistration: [] },
  } = useGetAssetRegistrationQuery({
    fetchPolicy: 'network-only',
    variables: {
      CompanyID: CompanyID,
    },
  })

  const [editDataLoaded, setEditDataLoaded] = useState(false)

  const [
    getCurrentAssetValueInfoForRevaluation,
    { loading: assetValueLoading },
  ] = useGetCurrentAssetValueInfoForRevaluationLazyQuery({
    fetchPolicy: 'network-only',
    onError: ({ message }) => {
      const error = message?.startsWith('GraphQL error: ')
        ? message.substring(15)
        : message
      setErrMsg(error)
      setErrorDia(true)
    },
    onCompleted: data => {
      if (data?.getCurrentAssetValueInfoForRevaluation) {
        const info = data.getCurrentAssetValueInfoForRevaluation
        setCurrentAssetInfo(info as CurrentAssetValueInfo)

        setValue('asset_cost', info.currentOriginalValue)
        setValue(
          'accumulated_depreciation',
          info.currentAccumulatedDepreciation
        )
        setValue('net_amount_before', info.currentNetBookValue)
        const isEditMode = formMode === 'edit' || formMode === 'resubmit'
        if (
          isEditMode &&
          editData?.net_amount_after !== undefined &&
          !editDataLoaded
        ) {
          setValue('net_amount_after', editData.net_amount_after)
          setEditDataLoaded(true)
        } else if (!isEditMode) {
          setValue('net_amount_after', info.currentNetBookValue)
        }
      }
    },
  })

  const {
    loading: openPeriodCheckDateLoading,
    data: { latestOpenPeriodCheckingDate } = {
      latestOpenPeriodCheckingDate: {} as any,
    },
  } = useLatestOpenPeriodCheckingDateQuery({
    fetchPolicy: 'network-only',
    variables: { CompanyID },
    onError: ({ message }) => {
      const error = message?.substring(15)
      setErrMsg(error)
      setErrorDia(true)
    },
  })

  const {
    loading: docDateValidationLoading,
    data: { getDocumentDateValidation } = { getDocumentDateValidation: [] },
  } = useGetDocumentDateValidationQuery({
    fetchPolicy: 'network-only',
    onError: ({ message }) => {
      const error = message?.substring(15)
      setErrMsg(error)
      setErrorDia(true)
    },
  })

  const assetsList = useMemo(() => {
    return (getAssetRegistration || []).filter(
      (a: any) => a.ApprovalStatus === 'COMPLETED'
    )
  }, [getAssetRegistration])

  const [
    createRevaluation,
    { loading: createLoading },
  ] = useCreateFaAssetRevaluationMutation({
    onError: ({ message }) => {
      const error = message?.startsWith('GraphQL error: ')
        ? message.substring(15)
        : message
      setErrMsg(error)
      setErrorDia(true)
    },
    onCompleted: () => {
      setOpenSnackBar(true)
      setSnackBarMsg(SystemMsgs.createNewRecord())
      history.push(`/fixed-assets/${CompanyID}/revaluation`)
    },
  })

  const [
    updateRevaluation,
    { loading: updateLoading },
  ] = useUpdateFaAssetRevaluationMutation({
    onError: ({ message }) => {
      const error = message?.startsWith('GraphQL error: ')
        ? message.substring(15)
        : message
      setErrMsg(error)
      setErrorDia(true)
    },
    onCompleted: () => {
      setOpenSnackBar(true)
      setSnackBarMsg(SystemMsgs.updateRecord())
      history.push(`/fixed-assets/${CompanyID}/revaluation`)
    },
  })

  useEffect(() => {
    if (currentAssetInfo) {
      const netAmountAfter = safeParseNumber(watchNetAmountAfter)
      const netAmountBefore = currentAssetInfo.currentNetBookValue

      const difference = netAmountAfter - netAmountBefore

      if (difference > 0) {
        setValue('revaluation_surplus', difference)
        setValue('revaluation_deficit', 0)
      } else if (difference < 0) {
        setValue('revaluation_surplus', 0)
        setValue('revaluation_deficit', Math.abs(difference))
      } else {
        setValue('revaluation_surplus', 0)
        setValue('revaluation_deficit', 0)
      }
    }
  }, [watchNetAmountAfter, currentAssetInfo, setValue])

  useEffect(() => {
    if (
      (formMode === 'edit' || formMode === 'resubmit') &&
      editData?.fa_asset_id &&
      assetsList.length > 0
    ) {
      const asset = assetsList.find(
        (a: any) => a.fa_asset_id === editData.fa_asset_id
      )
      if (asset) {
        setSelectedAsset(asset)
        setValue('asset_name', asset.name || '')
        setValue(
          'start_date',
          asset.start_use_date ? new Date(asset.start_use_date) : null
        )
        setValue('model_no', asset.model_no || '')
        setValue('category_name', asset.Category?.name || '')

        if (editData.revaluation_date) {
          setValue('revaluation_date', new Date(editData.revaluation_date))
        }
        if (editData.remark) {
          setValue('remark', editData.remark)
        }

        getCurrentAssetValueInfoForRevaluation({
          variables: {
            fa_asset_id: asset.fa_asset_id,
            exclude_revaluation_id: editData.revaluation_id || null,
          },
        })
      }
    }
  }, [formMode, editData, assetsList])

  const handleAssetChange = (asset: any) => {
    if (asset) {
      setSelectedAsset(asset)
      setValue('asset_no', asset)
      setValue('asset_name', asset.name || '')
      setValue(
        'start_date',
        asset.start_use_date ? new Date(asset.start_use_date) : null
      )
      setValue('model_no', asset.model_no || '')
      setValue('category_name', asset.Category?.name || '')

      getCurrentAssetValueInfoForRevaluation({
        variables: {
          fa_asset_id: asset.fa_asset_id,
          exclude_revaluation_id: editData?.revaluation_id || null,
        },
      })
    } else {
      setSelectedAsset(null)
      setCurrentAssetInfo(null)
      setValue('asset_no', null)
      setValue('asset_cost', 0)
      setValue('accumulated_depreciation', 0)
      setValue('net_amount_before', 0)
      setValue('net_amount_after', 0)
      setValue('revaluation_surplus', 0)
      setValue('revaluation_deficit', 0)
    }
  }

  const onSubmit = (
    data: RevaluationFormData,
    approvalStatus: ApprovalStatus
  ) => {
    if (approvalStatus === ApprovalStatus.Submit) {
      if (isPeriodClosed) {
        setErrMsg(
          'Cannot submit. The accounting period is closed or does not exist. ' +
            'Please select a date within the open financial period ' +
            (latestOpenPeriodCheckingDate?.StartDate &&
            latestOpenPeriodCheckingDate?.EndDate
              ? `(${latestOpenPeriodCheckingDate.StartDate} to ${latestOpenPeriodCheckingDate.EndDate}).`
              : '.')
        )
        setErrorDia(true)
        return
      }

      if (isDocDateInvalid) {
        setErrMsg(
          'Cannot submit. The revaluation date is outside the allowed validation range. ' +
            'Please select a date within the valid period.'
        )
        setErrorDia(true)
        return
      }
    }

    const currentAsset = selectedAsset || editData?.Asset

    if (!currentAsset) {
      setErrMsg('Please select an asset')
      setErrorDia(true)
      return
    }

    if (!currentAssetInfo) {
      setErrMsg('Please wait for asset information to load')
      setErrorDia(true)
      return
    }

    const revaluationDate = new Date(data.revaluation_date)
    const assetStartDate = currentAssetInfo.assetStartDate
      ? new Date(currentAssetInfo.assetStartDate)
      : null

    if (assetStartDate && revaluationDate < assetStartDate) {
      setErrMsg(
        `Revaluation date (${formatDateForDisplay(
          revaluationDate
        )}) cannot be before ` +
          `asset start use date (${formatDateForDisplay(assetStartDate)}).`
      )
      setErrorDia(true)
      return
    }

    if (currentAssetInfo.lastTransactionDate) {
      const lastTxnDate = new Date(currentAssetInfo.lastTransactionDate)
      if (revaluationDate < lastTxnDate) {
        setErrMsg(
          `Revaluation date (${formatDateForDisplay(
            revaluationDate
          )}) cannot be before ` +
            `the last transaction date (${formatDateForDisplay(
              lastTxnDate
            )}). ` +
            `Please use a date on or after the last transaction.`
        )
        setErrorDia(true)
        return
      }
    }

    const netAmountAfter = safeParseNumber(data.net_amount_after)

    if (netAmountAfter < 0) {
      setErrMsg('Net amount after revaluation cannot be negative.')
      setErrorDia(true)
      return
    }

    const difference = netAmountAfter - currentAssetInfo.currentNetBookValue
    const revaluationSurplus = difference > 0 ? difference : 0
    const revaluationDeficit = difference < 0 ? Math.abs(difference) : 0

    const input = {
      revaluation_id: editData?.revaluation_id || undefined,
      fa_asset_id: currentAsset.fa_asset_id,
      company_id: CompanyID,
      revaluation_date: data.revaluation_date,
      remark: data.remark,
      asset_cost: currentAssetInfo.currentOriginalValue,
      accumulated_depreciation: currentAssetInfo.currentAccumulatedDepreciation,
      net_amount_before: currentAssetInfo.currentNetBookValue,
      net_amount_after: netAmountAfter,
      revaluation_surplus: revaluationSurplus,
      revaluation_deficit: revaluationDeficit,
      ApprovalStatus: approvalStatus,
    }

    if (formMode === 'add') {
      createRevaluation({ variables: { input } })
    } else {
      updateRevaluation({ variables: { input } })
    }
  }

  const changeDateStr = (() => {
    const d = new Date(
      watchRevaluationDate || editData?.revaluation_date || new Date()
    )
    const year = d.getFullYear()
    const month = (d.getMonth() + 1).toString().padStart(2, '0')
    const day = d
      .getDate()
      .toString()
      .padStart(2, '0')
    return `${year}-${month}-${day}`
  })()

  const isPeriodClosed =
    latestOpenPeriodCheckingDate?.StartDate &&
    latestOpenPeriodCheckingDate?.EndDate
      ? changeDateStr < latestOpenPeriodCheckingDate?.StartDate ||
        changeDateStr > latestOpenPeriodCheckingDate?.EndDate
      : false

  const today = new Date()
  const monthsBefore = getDocumentDateValidation?.[0]?.MonthsBefore ?? 12
  const monthsAfter = getDocumentDateValidation?.[0]?.MonthsAfter ?? 1

  const minValidDate = new Date(today)
  minValidDate.setMonth(minValidDate.getMonth() - monthsBefore)
  minValidDate.setHours(0, 0, 0, 0)

  const maxValidDate = new Date(today)
  maxValidDate.setMonth(maxValidDate.getMonth() + monthsAfter)
  maxValidDate.setHours(23, 59, 59, 999)

  const changeDate = new Date(watchRevaluationDate || new Date())
  const isDocDateInvalid =
    changeDate.getTime() < minValidDate.getTime() ||
    changeDate.getTime() > maxValidDate.getTime()

  const getRevaluationTypeIndicator = () => {
    const netAmountAfter = safeParseNumber(watchNetAmountAfter)
    const netAmountBefore = currentAssetInfo?.currentNetBookValue || 0

    if (netAmountAfter > netAmountBefore) {
      return { type: 'Surplus', color: 'green' }
    } else if (netAmountAfter < netAmountBefore) {
      return { type: 'Deficit', color: 'red' }
    }
    return { type: 'No Change', color: '#666' }
  }

  const revaluationType = getRevaluationTypeIndicator()

  const draftFooterOption = {
    name: 'Save as Draft',
    onClick: handleSubmit(data => onSubmit(data, ApprovalStatus.Active)),
    color: 'primary',
    props: { type: 'button' },
  }

  const submitFooterOption = {
    name: 'Submit',
    onClick: handleSubmit(data => onSubmit(data, ApprovalStatus.Submit)),
    color: 'primary',
    props: { type: 'button' },
  }

  let footerOptions: any[]
  if (formMode === 'add' || formMode === 'edit' || formMode === 'resubmit') {
    footerOptions = [draftFooterOption, submitFooterOption]
  } else {
    footerOptions = []
  }

  return (
    <>
      {(getAssetRegistrationLoading ||
        createLoading ||
        updateLoading ||
        assetValueLoading) && <Loading />}

      <MainHeader
        mainBtn="close"
        onClick={() => {
          history.push(`/fixed-assets/${CompanyID}/revaluation`)
        }}
        smTitle={'Fixed Assets'}
        title={user?.companyName}
        routeSegments={[
          { name: 'Fixed Assets' },
          { name: 'Revaluation', current: true },
        ]}
        rightRouteSegments={[
          {
            name:
              formMode === 'add'
                ? 'New'
                : formMode === 'resubmit'
                ? 'Resubmit'
                : 'Edit',
            current: true,
          },
        ]}
      />

      <ContentWrapper float footer>
        <CardContents>
          <Controller
            render={({ value, onChange }) => {
              const defVal = editData?.Asset || null

              return (
                <Autocomplete
                  options={
                    assetsList?.sort((a: any, b: any) => {
                      return a?.DocNo?.localeCompare(b?.DocNo)
                    }) || []
                  }
                  getOptionLabel={(option: any) =>
                    `${option?.DocNo} - ${option?.name}`
                  }
                  fullWidth
                  onChange={(event, value: any) => handleAssetChange(value)}
                  defaultValue={defVal}
                  disabled={mode === 'approve-reject'}
                  renderOption={(props: any) => (
                    <div>
                      <div>
                        <span className="xsTitle">{props?.DocNo}</span>
                        <span className="desc" style={{ marginLeft: '8px' }}>
                          {props?.name}
                        </span>
                      </div>
                      <div>
                        <span className="desc" style={{ color: '#888' }}>
                          {props?.Category?.name}
                        </span>
                      </div>
                    </div>
                  )}
                  renderInput={(params: any) => (
                    <TextField
                      {...params}
                      label="Asset"
                      style={{ width: '100%' }}
                      required
                      margin="normal"
                      helperText={errors?.asset_no?.message}
                      error={errors?.asset_no ? true : false}
                    />
                  )}
                />
              )
            }}
            name="asset_no"
            control={control}
            margin="normal"
            defaultValue={editData?.Asset || null}
          />

          <Controller
            as={TextField}
            name="asset_name"
            label="Asset Name"
            autoComplete="off"
            control={control}
            fullWidth
            margin="dense"
            defaultValue={editData?.Asset?.name || 'N/A'}
            InputProps={{
              disableUnderline: true,
              readOnly: true,
            }}
          />

          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <Controller
              render={({ value, onChange }) => (
                <KeyboardDatePicker
                  value={value}
                  onChange={onChange}
                  label="Asset Start Date"
                  format="dd/MM/yyyy"
                  readOnly
                  margin="dense"
                  allowKeyboardControl
                  KeyboardButtonProps={{
                    style: { display: 'none' },
                  }}
                  className="left"
                  InputProps={{
                    disableUnderline: true,
                    readOnly: true,
                  }}
                />
              )}
              name="start_date"
              control={control}
              defaultValue={
                editData?.Asset?.start_use_date
                  ? new Date(editData.Asset.start_use_date)
                  : new Date()
              }
            />
          </MuiPickersUtilsProvider>

          <Controller
            as={TextField}
            name="category_name"
            label="Category"
            autoComplete="off"
            control={control}
            fullWidth
            margin="dense"
            className="right"
            defaultValue={editData?.Asset?.Category?.name || 'N/A'}
            InputProps={{
              disableUnderline: true,
              readOnly: true,
            }}
          />

          <Controller
            render={({ value, onChange }) => (
              <NumberFormat
                customInput={TextField as any}
                value={value}
                thousandSeparator
                decimalScale={2}
                fixedDecimalScale
                label="Current Original Value"
                fullWidth
                margin="dense"
                className="left"
                InputProps={{
                  disableUnderline: true,
                  readOnly: true,
                }}
              />
            )}
            name="asset_cost"
            control={control}
            defaultValue={editData?.asset_cost || 0}
          />

          <Controller
            render={({ value, onChange }) => (
              <NumberFormat
                customInput={TextField as any}
                value={value}
                thousandSeparator
                decimalScale={2}
                fixedDecimalScale
                label="Current Accumulated Depreciation"
                fullWidth
                margin="dense"
                className="right"
                InputProps={{
                  disableUnderline: true,
                  readOnly: true,
                }}
              />
            )}
            name="accumulated_depreciation"
            control={control}
            defaultValue={editData?.accumulated_depreciation || 0}
          />

          <Controller
            render={({ value, onChange }) => (
              <NumberFormat
                customInput={TextField as any}
                value={value}
                thousandSeparator
                decimalScale={2}
                fixedDecimalScale
                label="Current Net Book Value (Before Revaluation)"
                fullWidth
                margin="dense"
                InputProps={{
                  disableUnderline: true,
                  readOnly: true,
                }}
                helperText={
                  currentAssetInfo
                    ? `Last Transaction: ${currentAssetInfo.lastTransactionDate ||
                        'N/A'}`
                    : ''
                }
              />
            )}
            name="net_amount_before"
            control={control}
            defaultValue={editData?.net_amount_before || 0}
          />
        </CardContents>

        <CardContents
          section={{
            header: { title: 'Revaluation Details' },
          }}
        >
          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <Controller
              render={({ value, onChange }) => (
                <KeyboardDatePicker
                  value={value}
                  onChange={onChange}
                  label="Revaluation Date"
                  format="dd/MM/yyyy"
                  margin="dense"
                  allowKeyboardControl
                  KeyboardButtonProps={{
                    'aria-label': 'change date',
                  }}
                  required
                  disabled={mode === 'approve-reject'}
                  minDate={
                    currentAssetInfo?.assetStartDate
                      ? new Date(currentAssetInfo.assetStartDate)
                      : undefined
                  }
                  minDateMessage="Revaluation date cannot be before asset start date"
                />
              )}
              name="revaluation_date"
              control={control}
              defaultValue={
                editData?.revaluation_date
                  ? new Date(editData.revaluation_date)
                  : new Date()
              }
            />
          </MuiPickersUtilsProvider>

          <Controller
            render={({ value, onChange }) => (
              <NumberFormat
                customInput={TextField as any}
                value={value}
                onValueChange={({ floatValue }) => onChange(floatValue || 0)}
                thousandSeparator
                decimalScale={2}
                fixedDecimalScale
                label="Revised Net Book Value (After Revaluation)"
                fullWidth
                margin="dense"
                required
                disabled={mode === 'approve-reject'}
                error={errors?.net_amount_after ? true : false}
                // helperText={
                //   errors?.net_amount_after?.message || (
                //     <span style={{ color: revaluationType.color }}>
                //       {revaluationType.type}:{' '}
                //       {formatCurrency(
                //         revaluationType.type === 'Surplus'
                //           ? safeParseNumber(watchNetAmountAfter) -
                //               (currentAssetInfo?.currentNetBookValue || 0)
                //           : revaluationType.type === 'Deficit'
                //           ? (currentAssetInfo?.currentNetBookValue || 0) -
                //             safeParseNumber(watchNetAmountAfter)
                //           : 0
                //       )}
                //     </span>
                //   )
                // }
              />
            )}
            name="net_amount_after"
            control={control}
            defaultValue={editData?.net_amount_after || 0}
          />

          <Controller
            render={({ value, onChange }) => (
              <NumberFormat
                customInput={TextField as any}
                value={value}
                thousandSeparator
                decimalScale={2}
                fixedDecimalScale
                label="Revaluation Surplus"
                fullWidth
                margin="dense"
                className="left"
                InputProps={{
                  disableUnderline: true,
                  readOnly: true,
                  style: { color: 'green' },
                }}
              />
            )}
            name="revaluation_surplus"
            control={control}
            defaultValue={editData?.revaluation_surplus || 0}
          />

          <Controller
            render={({ value, onChange }) => (
              <NumberFormat
                customInput={TextField as any}
                value={value}
                thousandSeparator
                decimalScale={2}
                fixedDecimalScale
                label="Revaluation Deficit"
                fullWidth
                margin="dense"
                className="right"
                InputProps={{
                  disableUnderline: true,
                  readOnly: true,
                  style: { color: 'red' },
                }}
              />
            )}
            name="revaluation_deficit"
            control={control}
            defaultValue={editData?.revaluation_deficit || 0}
          />

          <VoiceTextField
            mounted={true}
            label="Remark"
            name="remark"
            setValue={setValue}
            record={record}
            setRecord={setRecord}
            control={control}
            controllerProps={{
              autoComplete: 'off',
              defaultValue: editData?.remark || '',
            }}
            disabled={mode === 'approve-reject'}
          />
        </CardContents>
      </ContentWrapper>

      {footerOptions?.length > 0 && (
        <AccountFooter options={[...footerOptions]} />
      )}

      <ErrorDialog
        errorDia={errorDia}
        setErrorDia={setErrorDia}
        errorMsg={errMsg}
        errorHeaderMsg={'Error'}
      />

      <ExitConfirmationDialog
        openExitConf={openExitConf}
        setOpenExitConf={setOpenExitConf}
        onConfirm={() => {
          history.push(`/fixed-assets/${CompanyID}/revaluation`)
        }}
      />
    </>
  )
}
